import {emptyByDefault} from '@optimaxdev/analytics/desktop/utils/emptyByDefault/emptyByDefault';
import {convertPriceEEC} from '@optimaxdev/utils';

import {CATEGORY_LIST} from 'constants/analytics/storages';
import {PRODUCT_TYPES} from 'constants/product';
import {CheckoutItemType, ProductType} from 'desktop/types/checkoutItemsType';
import {localStore} from 'libs/storage/storage';

/**
 * Get brand name
 * @param {ProductType} product - product
 * @returns {string} - brand name
 */
export const getBrandName = (product: ProductType): string => {
    if (product.brand && typeof product.brand === 'object')
        return emptyByDefault(product.brand.label);

    return emptyByDefault(product.brand_value || (product.brand as unknown as string));
};

/**
 * Get product category
 * @param {CheckoutItemType} item - Product data.
 * @returns {string} Product category
 */
export const prepareCategory = (item: CheckoutItemType): string => {
    const {id, type} = item.product;
    if (type === PRODUCT_TYPES.CONTACT_LENSES) return 'Contact Lenses';
    const categoryList = localStore.get(CATEGORY_LIST) || [];
    const {category} = categoryList?.find(categoryItem => categoryItem?.id === id) || {
        category: 'Non category page',
    };
    return category;
};

/**
 * Return true if glasses size contain "Little Kids"
 * @type {Function}
 * @param {ProductType} product field
 * @return {boolean} is kids glasses
 */
const isKidsGlasses = (product: ProductType) => /Little Kids/.test(product.size);

/**
 * Return first letter of frame size
 * @type {Function}
 * @param {ProductType} product field
 */
const getFrameSizeByFrameSize = (product: ProductType) => emptyByDefault(product.size).slice(0, 1);

/**
 * Return product variant based on frame size and frame color
 * Example: L|Black
 * @type {Function}
 * @param {ProductType} item - checkout item
 * @return {string} variant
 */
export const getVariantByCheckoutItem = (item: ProductType) => {
    if (item?.type === 'contact_lenses') {
        return `C|${emptyByDefault(item?.contactLensType)}`;
    }

    if (item && isKidsGlasses(item)) {
        return `T|${item?.color}`;
    }

    return `${getFrameSizeByFrameSize(item)}|${item?.color}`;
};

/**
 * Return product price
 * Example: L|Black
 * @param {CheckoutItemType} item - checkout item
 * @return {number} variant
 */
export const getItemPrice = (item: CheckoutItemType) =>
    convertPriceEEC(
        item.product.type === PRODUCT_TYPES.CONTACT_LENSES
            ? Number(item.discountedPrice || 0)
            : Number(item.discountedPrice || 0) * item.qty,
    );
